<template>
  <div class="with-side-panel">
    <div class="content-i">
      <div class="content-box">
        <div class="element-wrapper">
          <h6 class="element-header">
            {{ $t('dashboard.dashboard_for') + piniaUserStore().currentUser?.user?.userAccessRightsTranslation }}
          </h6>

          <Menu/>

          <div class="element-box mt-4">
            <div class="row mt-2">
              <CoachingButton
                  :enabled="companySettings?.privateCoachingEnabled"
                  route-name="privateCoachingNewAndActiveList"
                  coaching-type="private"
                  label="Einzelcoaching"/>
              <CoachingButton
                  :enabled="companySettings?.groupCoachingEnabled"
                  route-name="groupCoachingNewAndActiveList"
                  coaching-type="group"
                  label="Gruppenmaßnahmen"/>
            </div>
          </div>

          <h6 class="mt-5">{{ $t('general.active_customers') }}</h6>
          <div class="row mt-3">
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CardInfo :label="'Einzelcoaching'" :value="infoData?.countActiveCoaching?.counts?.privateCoaching"/>
            </div>
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CardInfo :label="'Gruppenmaßnahmen'" :value="infoData?.countActiveCoaching?.counts?.groupCoaching"/>
            </div>
          </div>

          <h6 class="mt-4">Übersicht Standorte</h6>
          <div class="row mt-3">
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CardInfo :label="'Anzahl interne Standorte'" :value="infoData?.countInternalOffices"/>
            </div>
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CardInfo :label="'Anzahl externe Standorte'" :value="infoData?.countExternalOffices"/>
            </div>
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CardInfo :label="'Anzahl sonstige'" :value="infoData?.countOtherOffices"/>
            </div>
          </div>

          <div class="element-box mb-3 mt-4">
            <h6>Einheiten im Jahr {{ filterData.year }} fertiggestellt</h6>
            <div class="row mt-4">
              <div class="col-sm-12">
                <div class="row">
                  <div class="col-lg-3 col-md-4 col-sm-12">
                    <SelectVue
                        v-model="filterData.coachingType"
                        :options="filteredCoachingTypes"
                        :is-clear="false"
                    />
                  </div>
                  <div class="col-lg-2 col-md-4 col-sm-12">
                    <SelectVue
                        v-model="filterData.year"
                        :options="dropdownYears"
                        :is-clear="false"
                    />
                  </div>
                  <div class="col-lg col-md-4 col-sm-12">
                    <SelectVue
                        v-model="filterData.locationId"
                        placeholder="Standort"
                        :options="dropdownOffices"
                        :is-clear="true"
                    />
                  </div>

                  <div class="col-auto text-center">
                    <button class="btn btn-primary custom_search_button2" @click="getChartBar(true)">
                      <span v-if="isSearching">
                        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        {{ $t('general.button.search_in_progress') }}
                      </span>
                      <span v-else>
                        {{ $t('general.button.search') }}
                      </span>
                    </button>
                  </div>
                </div>
                <VueApexCharts width="100%" type="bar" :options="chartOptionsBar" :series="seriesSignBar"/>
              </div>
            </div>
          </div>
        </div>
      </div>
      <FormSidebar>
        <template #top>
          <h6 class="element-header">
            Sidebar
          </h6>
        </template>

        <div class="element-box pie mt-4">
          <h6>{{ $t('general.active_customers') }}</h6>
          <VueApexCharts width="100%" type="donut" :options="chartOptions" :series="seriesActiveCoach"/>
        </div>
        <div class="element-box">
          <h6>Standorte nach Bundesländern</h6>
          <div v-for="user of infoData?.countOfficeByFederalState" v-bind:key="user?.name"
               class="d-flex justify-content-between">
            <span>{{ user?.federalState }}</span>
            <span>{{ user?.count }}</span>
          </div>
        </div>

        <div class="element-box">
          <h6>{{ $t('general.birthdays') }}</h6>
          <div v-for="user of infoData?.todayBirthdays" v-bind:key="user?.name" class="d-flex justify-content-between">
            <span>{{ user?.fullName }}</span>
            <span>{{ user?.date }}</span>
          </div>
        </div>
      </FormSidebar>
    </div>
  </div>
</template>

<script setup>
import dashboardHeadAMDLService from '@/api/Dashboard/HeadAMDL';
import CardInfo from '@/components/card/CardInfo.vue';
import CoachingButton from '@/components/button/CoachingButton.vue';
import FormSidebar from '@/components/FormSidebar.vue';
import SelectVue from '@/components/SelectVue.vue';
import { ErrorToast } from '@/components/VueToast';
import { getCoachingTypes, getOfficeDropdown, getYears } from '@/shared/dropdowns';
import { optionsChartBar, optionsChartDonut } from '@/shared/utils/apex';
import { MonthsUtils } from '@/shared/utils/MonthsUtils';
import { piniaCompanySettingsStore } from '@/store/piniaCompanySettingsStore';
import { piniaUserStore } from '@/store/piniaUserStore';
import Menu from '@/views/App/Dashboard/HeadAMDL/Menu.vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import VueApexCharts from 'vue3-apexcharts';

const { t }             = useI18n();

const piniaCompanySettingsState = piniaCompanySettingsStore();
const companySettings = computed(() => piniaCompanySettingsState.companySettings);

const chartOptions      = ref(optionsChartDonut);
const seriesActiveCoach = ref([]);

const seriesSignBar = ref([]);

const chartOptionsBar = ref({
  ...optionsChartBar,
  xaxis: {
    categories: MonthsUtils(t),
  },
  colors: [
    'rgba(67, 173, 78, 0.6)',
    'rgba(75, 192, 192, 0.6)',
  ],
  plotOptions: {
    bar: {
      distributed: false,
    },
  },
});

const dropdownYears        = ref([]);
const dropdownCoachingType = ref([]);
const dropdownOffices      = ref([]);
const isSearching          = ref(false);

const filterData = ref({
  coachingType: 'private_coaching',
  year: new Date().getFullYear(),
  locationId: '',
});

const infoData = ref({});

async function getInfoData() {
  try {
    const { data } = await dashboardHeadAMDLService.getInfoData();
    infoData.value = data.data;

    seriesActiveCoach.value = Object.values(data.data.countActiveCoaching?.counts);
    chartOptions.value      = {
      ...chartOptions.value,
      responsive: [ {
        breakpoint: 2480,
        options: {
          chart: {
            width: '100%',
            height: 300,
          },
          legend: {
            position: 'bottom',
          },
        },
      } ],
      labels: [
        `Einzelcoaching ${data.data.countActiveCoaching.counts?.privateCoaching}`,
        `Gruppenmaßnahmen ${data.data.countActiveCoaching.counts?.groupCoaching}`,
      ],
      plotOptions: {
        pie: {
          donut: {
            ...chartOptions.value.plotOptions.pie.donut,
            labels: {
              ...chartOptions.value.plotOptions.pie.donut.labels,
              total: {
                ...chartOptions.value.plotOptions.pie.donut.labels.total,
                formatter: () => data.data.countActiveCoaching?.total.toString(),
              },
            },
          },
        },
      },
    };
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getChartBar() {
  try {
    isSearching.value   = true;
    const { data }      = await dashboardHeadAMDLService.getUnitsDoneChart(filterData.value);
    seriesSignBar.value = [ data.data.chartData.signedUnits, data.data.chartData.unsignedUnits ];
    isSearching.value   = false;
  } catch (error) {
    isSearching.value = false;
    await ErrorToast(error);
  }
}

const filteredCoachingTypes = computed(() =>
    dropdownCoachingType.value.filter(
        item => item.value === 'private_coaching' || item.value === 'group_coaching',
    ),
);

onMounted(() => {
  getInfoData();
  getChartBar();
  getYears(dropdownYears);
  getCoachingTypes(dropdownCoachingType);
  getOfficeDropdown(dropdownOffices);
});
</script>

<style scoped>
::v-deep(.pie .apexcharts-legend) {
  display: block !important;
}
</style>